"use client";

import { useEffect } from "react";

import { getStaleServerActionId } from "@/utils/skewWatcher/getStaleServerActionId";
import { reportServerActionSkew } from "@/utils/skewWatcher/reportServerActionSkew";

// Watches server-action replies globally because a stale action fails without throwing, so no call-site try/catch (and therefore no logError) ever sees it. Read-only: the caller always gets the untouched response.
export function ServerActionSkewWatcher(): null {
  useEffect(() => {
    const originalFetch = window.fetch;
    // Cleanup can't always take the wrapper out of the chain (another library may have wrapped fetch after us), so it deactivates it instead; otherwise a remount would report every stale reply twice
    let isActive = true;

    const watchedFetch: typeof window.fetch = async (input, init) => {
      // Deliberately not caught: a network failure has to reach the caller exactly as it would without the watcher
      const response = await originalFetch(input, init);

      if (!isActive) {
        return response;
      }

      try {
        // Per the fetch spec, init.headers replaces the headers of a Request passed as input, so it is the effective set
        const requestHeaders = new Headers(
          init?.headers ??
            (input instanceof Request ? input.headers : undefined)
        );

        const staleServerActionId = getStaleServerActionId(
          requestHeaders,
          response
        );
        if (staleServerActionId !== null) {
          reportServerActionSkew(staleServerActionId);
        }
      } catch (watcherError) {
        // The watcher must never break the request it is observing
        console.error("Server action skew watcher failed:", watcherError);
      }

      return response;
    };

    window.fetch = watchedFetch;

    return () => {
      isActive = false;
      // Another library may have wrapped fetch after us; restoring then would silently drop its wrapper
      if (window.fetch === watchedFetch) {
        window.fetch = originalFetch;
      }
    };
  }, []);

  return null;
}
